import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFeedbackRules,
  createFeedbackRule,
  patchFeedbackRule,
  deleteFeedbackRule,
  getPendingFeedbackRules,
  previewRuleAgainstLibrary,
  applyRuleToLibrary,
} from '../api/feedbackRules';

const QUERY_KEY = ['content', 'feedback-rules'];
const PENDING_QUERY_KEY = ['content', 'feedback-rules', 'pending'];

export function useFeedbackRules(filters?: { category?: string; active?: boolean }) {
  return useQuery({
    queryKey: [...QUERY_KEY, filters],
    queryFn: () => getFeedbackRules(filters),
    staleTime: 2 * 60_000,
  });
}

export function usePendingFeedbackRules() {
  return useQuery({
    queryKey: PENDING_QUERY_KEY,
    queryFn: getPendingFeedbackRules,
    staleTime: 30_000,
  });
}

export function useCreateFeedbackRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFeedbackRule,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function usePatchFeedbackRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...updates
    }: {
      id: string;
      rule?: string;
      category?: string;
      priority?: number;
      isActive?: boolean;
      status?: 'pending' | 'active' | 'rejected';
    }) => patchFeedbackRule(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PENDING_QUERY_KEY });
    },
  });
}

export function useDeleteFeedbackRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFeedbackRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PENDING_QUERY_KEY });
    },
  });
}

export function useRuleLibraryPreview(ruleId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['content', 'feedback-rules', ruleId, 'library-preview'],
    queryFn: () => previewRuleAgainstLibrary(ruleId!),
    enabled: !!ruleId && enabled,
    staleTime: 30_000,
  });
}

export function useApplyRuleToLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, excludeContentItemIds }: { ruleId: string; excludeContentItemIds: string[] }) =>
      applyRuleToLibrary(ruleId, excludeContentItemIds),
    onSuccess: () => {
      // Touched items got a new version + body — refresh library + version queries.
      queryClient.invalidateQueries({ queryKey: ['content', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['content', 'item'] });
      queryClient.invalidateQueries({ queryKey: ['content', 'versions'] });
    },
  });
}
